import { useCallback, useRef, useState } from "react"
import { useField, useFormikContext } from "formik"

import { FinalSuccessDialogResources } from "../finalSuccessDialog/FinalSuccessDialogResources"
import { computeOrderFormStatus, OrderFormStatus } from "../../mainForm/helpers"
import { MeetingServiceOrder } from "../../../../../modules/merchant/accounting/accounting/accountingTypes"
import { useDateTime } from "../../../../../modules/localization/useDateTime"
import { CreateUpdateBooking, MeetingServicePriceListState, SelectedPriceListItem } from "../../createOrderTypes"
import type { CreateUpdateBookingMeetingDetails } from "../../../dialogs/createUpdateBookingDialog/createUpdateBookingTypes"
import { useCreateOrderFormConfig, useCreateOrderFormSetOrder } from "../../mainForm/CreateOrderFormContext"
import { DeliveryTimesField, DynamicConfigKey, DynamicMainFormValues, MainOrderFormikRef } from "../form/formTypes"
import { CreateUpdateBookingResponse } from "../../createOrderApi"
import { ServiceResponseStatus } from "../../../../../core/api/apiTypes"
import { BusyResourceDatesDialog } from "./BusyResourceDatesDialog"
import { useResourceBookingDraft } from "./ResourceBookingDraftContext"
import { useConfirmBookingDialogActions } from "./ResourceBookingConfirmDialogContext"
import { ResourceBooking } from "./ResourceBooking"
import { BookingMoveDeliverySyncDialog } from "./BookingMoveDeliverySyncDialog"
import type { BookingMove } from "./bookingMoveDeliverySyncHelpers"
import { hasDeliveryMismatch, shiftDeliveries } from "./bookingMoveDeliverySyncHelpers"
import { getMeetingLocationFromBookedResources } from "./helpers"
import { CreateOrderType } from "../../helpers"

import { getResponsibleSeed } from "../../../../../modules/order/pages/helpers"
import { defaultInitialEventState, defaultMultipleDeliveries, defaultOrderContextState } from "../../defaults"
import { useRefreshCalendar } from "./context/RefreshCalendarProvider"

type DeliverySyncRequest = Readonly<{
    move: BookingMove
    meetingServiceOrderId: number
    currency: string
    fallbackSeats: number
    priceList: SelectedPriceListItem
    suggestedDeliveries: DeliveryTimesField
    currentDeliveries: DeliveryTimesField
    syncKey: string
}>

type ResourceBookingWrapperProps = Readonly<{
    handleChangeOrderType: (type: CreateOrderType) => void
    orderFormStatusRef: React.MutableRefObject<OrderFormStatus>
    orderDetails?: MeetingServiceOrder
    outerFormik: MainOrderFormikRef
}>

// A booking whose response reported occurrences that could not be booked; shown before success.
type PendingBusyDates = Readonly<{
    dates: string[]
    details: CreateUpdateBookingResponse
    content: CreateUpdateBooking
    isBookingUpdate: boolean
}>

export function ResourceBookingWrapper({
    handleChangeOrderType,
    orderFormStatusRef,
    orderDetails,
    outerFormik,
}: ResourceBookingWrapperProps) {
    const { user, handleClose, handleOutlookBookingCompleted, availableModules } = useCreateOrderFormConfig()
    const { setOrderContext } = useCreateOrderFormSetOrder()
    const { setConfirmBookingDialog } = useConfirmBookingDialogActions()
    const [finalSuccessDialog, setFinalSuccessDialog] = useState<{
        response: CreateUpdateBookingResponse
        isBookingUpdate: boolean
    } | null>(null)
    const [pendingBusyDates, setPendingBusyDates] = useState<PendingBusyDates | null>(null)
    // The success dialog waits behind the delivery prompt so the two never stack.
    const [deferredSuccessDialog, setDeferredSuccessDialog] = useState<{
        response: CreateUpdateBookingResponse
        isBookingUpdate: boolean
    } | null>(null)
    const [deliverySync, setDeliverySync] = useState<DeliverySyncRequest | null>(null)

    const dateTimeFactory = useDateTime()

    // The booking time last saved in this session, so a second move is measured from the first one
    // and not from the order as it was loaded. Empty until then — the loaded order is the baseline.
    const savedBookingStartRef = useRef<string | undefined>(undefined)
    // Set when the user keeps the delivery as it is, so re-saving the same booking time is silent.
    const skippedSyncKeyRef = useRef<string | null>(null)

    const {
        draftEvent,
        fullResources: fullResourcesList,
        setDraftEvent,
        handleChangeInitEventState,
        initialEventStateId,
    } = useResourceBookingDraft()
    const [{ value: multipleDeliveries }, , multipleDeliveriesHelpers] = useField<DeliveryTimesField>(
        DynamicConfigKey.MULTIPLE_DELIVERIES,
    )
    const [{ value: priceListState }] = useField<MeetingServicePriceListState>("priceList")

    const { setValues } = useFormikContext<DynamicMainFormValues>()
    const { triggerRefetch } = useRefreshCalendar()

    const setMany = useCallback(
        (patch: Partial<DynamicMainFormValues>) => {
            setValues((prev) => ({ ...prev, ...patch }))
        },
        [setValues],
    )

    /**
     * A delivery may legitimately sit outside the meeting window, so only a delivery that landed on
     * another day than the booking — or fell out of the new window — is worth interrupting for.
     */
    const buildDeliverySyncRequest = (savedOrderId: number, meetingDetails: CreateUpdateBookingMeetingDetails) => {
        const { isFullInitOrder, meetingServiceId } = orderFormStatusRef.current
        const meetingServiceOrderId = isFullInitOrder ? savedOrderId : meetingServiceId
        const currency = orderDetails?.amount?.currency
        const priceList = priceListState?.item

        const baselineStart = savedBookingStartRef.current ?? orderDetails?.meetingDetails?.startDateTime

        // The prompt loads the shop's opening hours by merchant, so without one it has no rules to
        // validate the new delivery time against.
        if (!meetingServiceOrderId || !currency || !priceList?.merchantId) return null
        if (!multipleDeliveries?.deliveryDate || !multipleDeliveries.deliveryTimes.some(({ time }) => Boolean(time))) {
            return null
        }
        if (!baselineStart || !meetingDetails.startDateTime || !meetingDetails.endDateTime) return null

        const previousStart = dateTimeFactory.fromApi(baselineStart)
        const newStart = dateTimeFactory.fromApi(meetingDetails.startDateTime)
        const newEnd = dateTimeFactory.fromApi(meetingDetails.endDateTime)

        if (!previousStart.isValid || !newStart.isValid || !newEnd.isValid) return null

        const delta = newStart.diff(previousStart)
        if (delta.toMillis() === 0) return null

        const move: BookingMove = { previousStart, newStart, newEnd }
        if (!hasDeliveryMismatch(multipleDeliveries, move, dateTimeFactory)) return null

        const syncKey = `${meetingServiceOrderId}|${meetingDetails.startDateTime}|${meetingDetails.endDateTime}`
        if (skippedSyncKeyRef.current === syncKey) return null

        return {
            move,
            meetingServiceOrderId,
            currency,
            fallbackSeats: orderDetails?.deliveryDetails?.sittings ?? 0,
            priceList,
            suggestedDeliveries: shiftDeliveries(multipleDeliveries, delta, dateTimeFactory),
            currentDeliveries: multipleDeliveries,
            syncKey,
        }
    }

    const openSuccessDialog = (
        response: CreateUpdateBookingResponse,
        orderContent: CreateUpdateBooking,
        isBookingUpdate: boolean,
    ) => {
        const { order } = response
        const { id, meetingDetails, bookingDetails, messageToServiceCenter } = order

        const resourcesList = bookingDetails?.resources ?? []

        // The save response carries no customer, so the order being edited is what identifies the
        // people on it; on a create there is none, and the calendar entry is still id-less anyway.
        const person = getResponsibleSeed(order.organizers, orderDetails?.customer?.id)

        // Reconcile the draft with what was actually saved: the dialog can drop resources or move the
        // time before submitting, and the order query is not refetched while the dialog stays open.
        // The response carries resource ids but not their names or types, so the existing list stays
        // the source for those. An empty response means "nothing to reconcile", never "no booking".
        const savedResourceIds = resourcesList.map((resource) => String(resource.id))
        const savedFullResources = savedResourceIds.length
            ? (fullResourcesList ?? []).filter((resource) => savedResourceIds.includes(String(resource.id)))
            : (fullResourcesList ?? [])
        const savedStart = meetingDetails.startDateTime
            ? dateTimeFactory.toApiISOMinutesFormat(dateTimeFactory.fromApi(meetingDetails.startDateTime))
            : ""
        const savedEnd = meetingDetails.endDateTime
            ? dateTimeFactory.toApiISOMinutesFormat(dateTimeFactory.fromApi(meetingDetails.endDateTime))
            : ""
        const savedEvent = draftEvent
            ? {
                  ...draftEvent,
                  ...(savedResourceIds.length ? { resourceIds: savedResourceIds } : {}),
                  ...(savedStart && savedEnd ? { start: savedStart, end: savedEnd } : {}),
              }
            : null

        // Only moving an existing booking can leave its delivery behind. Attaching a booking to an
        // order that already had catering is not a move, even though it saves the same two things.
        const syncRequest = isBookingUpdate ? buildDeliverySyncRequest(id, meetingDetails) : null

        if (syncRequest) {
            setDeferredSuccessDialog({ response, isBookingUpdate })
            setDeliverySync(syncRequest)
        } else {
            setFinalSuccessDialog({ response, isBookingUpdate })
        }
        savedBookingStartRef.current = meetingDetails.startDateTime
        setDraftEvent(savedEvent, savedFullResources)
        handleChangeInitEventState({
            // A booking saved in this session becomes the new "Undo all" baseline. Without it the
            // baseline stays null while `initValues.id` still shows the button, so undo wipes.
            event: savedEvent,
            initValues: {
                id,
                meetingName: meetingDetails.meetingName || "",
                organizers: person!,
                attendees: meetingDetails.attendees || [],
                resources: resourcesList,
                messageToServiceCenter: messageToServiceCenter || "",
                isPrivate: Boolean(meetingDetails.isPrivate),
                isExternal: Boolean(meetingDetails.isExternal),
            },
            isUpdated: false,
            fullResources: savedFullResources,
        })

        const fullResourcesById = new Map((fullResourcesList ?? []).map((r) => [r.id, r]))
        const firstBookedRoomId = resourcesList.find((booked) => fullResourcesById.get(booked.id)?.type?.id === 1)?.id

        // The booking goes through this single patch rather than a separate `setFieldValue`: Formik
        // resolves `setValues(fn)` against the render-time state, so an earlier field write in the
        // same tick would be overwritten by it.
        const patch: Partial<DynamicMainFormValues> = {
            event: savedEvent,
            fullResourcesList: savedFullResources,
            resources: resourcesList,
            [DynamicConfigKey.MEETING_NAME]: meetingDetails.meetingName || "",
            [DynamicConfigKey.REGISTER_PARTICIPANTS]: meetingDetails.attendees || [],
            [DynamicConfigKey.PRIVATE_MEETING]: Boolean(meetingDetails.isPrivate),
            [DynamicConfigKey.EXTERNAL_MEETING]: Boolean(meetingDetails.isExternal),
            [DynamicConfigKey.DELIVERY_LOCATIONS]: firstBookedRoomId ? String(firstBookedRoomId) : "",
            // Reopening the dialog reads this back out of the outer form, so the save path has to
            // write it too — closing without saving already does.
            [DynamicConfigKey.SERVICE_CENTER_MESSAGE]: messageToServiceCenter || "",
        }

        if (person) {
            patch[DynamicConfigKey.MEETING_RESPONSIBLE] = person
        }

        setMany(patch)

        if (!multipleDeliveries?.deliveryDate) {
            const deliveryDate = orderContent.meetingDetails.startDateTime
            multipleDeliveriesHelpers.setValue({
                ...defaultMultipleDeliveries,
                deliveryDate: deliveryDate.split(" ")[0],
            })
        }

        if (handleOutlookBookingCompleted) {
            const meetingLocation = getMeetingLocationFromBookedResources({
                bookedResources: resourcesList,
                fullResourcesList,
            })

            handleOutlookBookingCompleted({
                orderId: id,
                startDateTime: meetingDetails.startDateTime,
                endDateTime: meetingDetails.endDateTime,
                meetingLocation,
            })
        }
    }

    // Recurring bookings may partially fail: the API reports the dates whose resources were busy.
    // Show those to the user (with a copy action) before the usual success dialog.
    const handleOpenSuccessDialog = (
        orderDetails: CreateUpdateBookingResponse,
        orderContent: CreateUpdateBooking,
        isBookingUpdate: boolean,
    ) => {
        const busyResourceDates =
            orderDetails.serviceResponse?.status === ServiceResponseStatus.PARTIAL_SUCCESS
                ? (orderDetails.resultDetails?.busyResourceDates ?? [])
                : []

        if (busyResourceDates.length > 0) {
            setPendingBusyDates({
                dates: busyResourceDates,
                details: orderDetails,
                content: orderContent,
                isBookingUpdate,
            })
            return
        }

        openSuccessDialog(orderDetails, orderContent, isBookingUpdate)
    }

    const handleBusyDatesContinue = () => {
        if (!pendingBusyDates) return
        const { details, content, isBookingUpdate } = pendingBusyDates
        setPendingBusyDates(null)
        openSuccessDialog(details, content, isBookingUpdate)
    }

    const handleClearResourceIds = () => {
        setDraftEvent(null)
        setConfirmBookingDialog(false)

        if (initialEventStateId) {
            handleChangeInitEventState({ isUpdated: true })
        }
    }

    const handleCloseFinalSuccessDialog = (shouldRefresh = true) => {
        setFinalSuccessDialog(null)
        shouldRefresh && triggerRefetch()
    }

    // Whether the delivery was updated or kept, the booking is saved either way — hand the flow back
    // to the success dialog that was waiting behind this prompt.
    const handleDeliverySyncResolved = () => {
        if (deliverySync) skippedSyncKeyRef.current = deliverySync.syncKey
        setDeliverySync(null)
        setFinalSuccessDialog(deferredSuccessDialog)
        setDeferredSuccessDialog(null)
    }

    const resetBookingToInitialState = () => {
        orderFormStatusRef.current = computeOrderFormStatus()
        setOrderContext(defaultOrderContextState)
        outerFormik.current?.resetForm()
        handleChangeInitEventState(defaultInitialEventState)
        setDraftEvent(null)
    }

    return (
        <>
            <ResourceBooking
                handleOpenSuccessDialog={handleOpenSuccessDialog}
                handleClearResourceIds={handleClearResourceIds}
                orderDetails={orderDetails}
                orderFormStatusRef={orderFormStatusRef}
            />
            {pendingBusyDates && (
                <BusyResourceDatesDialog open busyDates={pendingBusyDates.dates} onContinue={handleBusyDatesContinue} />
            )}
            {deliverySync && (
                <BookingMoveDeliverySyncDialog
                    open
                    move={deliverySync.move}
                    meetingServiceOrderId={deliverySync.meetingServiceOrderId}
                    userId={user.userId}
                    currency={deliverySync.currency}
                    fallbackSeats={deliverySync.fallbackSeats}
                    priceList={deliverySync.priceList}
                    suggestedDeliveries={deliverySync.suggestedDeliveries}
                    currentDeliveries={deliverySync.currentDeliveries}
                    onResolved={handleDeliverySyncResolved}
                />
            )}
            {finalSuccessDialog && (
                <FinalSuccessDialogResources
                    orderDetails={finalSuccessDialog.response}
                    isBookingUpdate={finalSuccessDialog.isBookingUpdate}
                    onClose={handleCloseFinalSuccessDialog}
                    handleCloseMainDialog={handleClose}
                    handleOpenMeetingService={handleChangeOrderType}
                    orderFormStatusRef={orderFormStatusRef}
                    resetBookingToInitialState={resetBookingToInitialState}
                    availableModules={availableModules}
                />
            )}
        </>
    )
}
